import { useCallback } from 'react';
import { PlanDetails, ServiceType } from '../types/api';
import { analyticsService } from '../services';
import { 
  sendInitiateCheckoutEvent, 
  sendSubscribeEvent 
} from '../services';
import { generateProductId, generateProductName, generateProductCategory, generateContentIds } from '../utils/productIdUtils';

export const useSubscriptionTracking = () => {
  const trackBeginCheckout = useCallback(async (
    planDetails: PlanDetails | null,
    subscriptionLevel: 'standard' | 'premium',
    subscriptionType: 'monthly' | 'yearly' | 'trial',
    serviceType: ServiceType,
    currency: string
  ) => {
    try {
      const planData = planDetails?.[subscriptionLevel];
      const price = subscriptionType === 'trial' 
        ? planData?.trial_price || 199
        : planData?.price || 599;

      const items = [{
        item_id: generateProductId(serviceType, subscriptionLevel, subscriptionType),
        item_name: generateProductName(serviceType, subscriptionLevel, subscriptionType),
        item_category: generateProductCategory(serviceType),
        item_brand: 'Piika',
        price: price,
        quantity: 1
      }];

      analyticsService.trackBeginCheckout(items, currency, price);
      console.log('📊 Tracked begin_checkout for subscription:', { subscriptionLevel, subscriptionType, items });
    } catch (analyticsError) {
      console.error('Failed to track begin checkout:', analyticsError);
    }
  }, []);

  const trackInitiateCheckout = useCallback(async (
    currency: string,
    priceBeforePromotion: number,
    serviceType: ServiceType,
    subscriptionLevel: 'standard' | 'premium',
    subscriptionType: 'monthly' | 'yearly' | 'trial'
  ) => {
    try {
      const userData = {
        clientUserAgent: navigator.userAgent
      };

      const customData = {
        currency: currency,
        value: String(priceBeforePromotion),
        content_ids: generateContentIds(serviceType, subscriptionLevel, subscriptionType),
        content_type: 'product'
      };

      await sendInitiateCheckoutEvent(
        userData,
        customData,
        window.location.href
      );
    } catch (eventError) {
      console.error('Error sending Facebook Initiate Checkout event:', eventError);
    }
  }, []);

  const trackSubscriptionEvents = useCallback(async (
    currency: string,
    calculatedTotal: number,
    subscriptionId: string,
    serviceType: ServiceType,
    subscriptionLevel: 'standard' | 'premium',
    subscriptionType: 'monthly' | 'yearly' | 'trial',
    selectedPaymentMethod: string,
    promotionCode: string | null
  ) => {
    try {
      // Track subscribe event
      const userData = {
        clientUserAgent: navigator.userAgent
      };

      const customData = {
        currency: currency,
        value: String(calculatedTotal),
        subscriptionId: subscriptionId,
        content_ids: generateContentIds(serviceType, subscriptionLevel, subscriptionType),
        content_type: 'product'
      };

      await sendSubscribeEvent(
        userData,
        customData,
        window.location.href
      );
      
      // Track subscription creation with Google Analytics
      analyticsService.trackSubscriptionCreation(
        subscriptionLevel,
        subscriptionType,
        selectedPaymentMethod,
        calculatedTotal,
        currency,
        subscriptionId
      );
      
      // Track purchase event for GA4 e-commerce
      analyticsService.trackPurchase(
        subscriptionId,
        [{
          item_id: generateProductId(serviceType, subscriptionLevel, subscriptionType),
          item_name: generateProductName(serviceType, subscriptionLevel, subscriptionType),
          item_category: generateProductCategory(serviceType),
          item_brand: 'Piika',
          price: calculatedTotal,
          quantity: 1
        }],
        currency,
        calculatedTotal,
        undefined, // tax
        undefined, // shipping
        'Piika', // affiliation
        promotionCode || undefined // coupon
      );
      
      // Track purchase conversion for Google Ads
      analyticsService.trackPurchaseConversion(subscriptionId);
    } catch (eventError) {
      console.error('Error sending tracking events:', eventError);
    }
  }, []);

  return {
    trackBeginCheckout,
    trackInitiateCheckout,
    trackSubscriptionEvents
  };
}; 